import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users, ShieldCheck, Loader2, AlertTriangle, Lock, Eye, BadgeCheck, RefreshCw,
  Fingerprint, Webhook, KeyRound, Plus, Trash2, Copy, Check, Send, RotateCw, Power,
  BarChart3, Gauge, DollarSign, Infinity as InfinityIcon, SlidersHorizontal,
} from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';

/**
 * Embeddable Admin Panel (iframe surface).
 *
 * Mounted at `/embed/v2/admin/:panel` (panel = `user-management` | `governance`)
 * behind the EmbedTokenProvider. A partner drops this into their own app in an
 * iframe and injects a DELEGATED ADMIN SESSION token (postMessage or ?token=).
 * The panel drives the same scoped `mgmt/v1/admin/*` endpoints the headless API
 * uses — so every action is bounded by the token's scopes + audited under its
 * act-as identity. No INZARA app chrome, no login flow.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

function makeCall(token, base) {
  return async (path, opts = {}) => {
    const res = await fetch(`${API}${base}${path}`, {
      ...opts,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(opts.headers || {}),
      },
    });
    if (res.status === 204) return {};
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg = body?.detail?.message || body?.detail || `${res.status} ${res.statusText}`;
      throw new Error(typeof msg === 'string' ? msg : `request failed (${res.status})`);
    }
    return res.json();
  };
}

function adminApi(token) {
  const call = makeCall(token, '/api/mgmt/v1/admin');
  return {
    listUsers: () => call('/users?limit=200'),
    listRoles: () => call('/roles'),
    setUserRole: (userId, role) => call(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
    listRls: () => call('/policies/rls'),
    toggleRls: (id) => call(`/policies/rls/${id}/toggle`, { method: 'PUT' }),
    listMasking: () => call('/policies/masking'),
    toggleMasking: (id) => call(`/policies/masking/${id}/toggle`, { method: 'PUT' }),
    listCertifications: () => call('/certifications'),
  };
}

function identityApi(token) {
  const call = makeCall(token, '/api/mgmt/v1/admin');
  return {
    listRoles: () => call('/roles'),
    getConfig: () => call('/identity/config'),
    setConfig: (body) => call('/identity/config', { method: 'PUT', body: JSON.stringify(body) }),
    listTokens: () => call('/identity/scim-tokens'),
    createToken: (name) => call('/identity/scim-tokens', { method: 'POST', body: JSON.stringify({ name }) }),
    revokeToken: (id) => call(`/identity/scim-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listMappings: () => call('/identity/group-mappings'),
    setMapping: (group, role) => call('/identity/group-mappings', { method: 'PUT', body: JSON.stringify({ group, role }) }),
    deleteMapping: (group) => call(`/identity/group-mappings/${encodeURIComponent(group)}`, { method: 'DELETE' }),
  };
}

function webhooksApi(token) {
  const call = makeCall(token, '/api/mgmt/v1/webhooks');
  return {
    listEvents: () => call('/events'),
    listEndpoints: () => call('/endpoints'),
    createEndpoint: (body) => call('/endpoints', { method: 'POST', body: JSON.stringify(body) }),
    updateEndpoint: (id, body) => call(`/endpoints/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    rotateSecret: (id) => call(`/endpoints/${encodeURIComponent(id)}/rotate-secret`, { method: 'POST' }),
    deleteEndpoint: (id) => call(`/endpoints/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    testEndpoint: (id) => call(`/endpoints/${encodeURIComponent(id)}/test`, { method: 'POST' }),
    listDeliveries: (endpointId) => call(`/deliveries?limit=50${endpointId ? `&endpoint_id=${encodeURIComponent(endpointId)}` : ''}`),
    replayDelivery: (id) => call(`/deliveries/${encodeURIComponent(id)}/replay`, { method: 'POST' }),
  };
}

function usageApi(token) {
  const call = makeCall(token, '/api/mgmt/v1/admin');
  const wsCall = makeCall(token, '/api/mgmt/v1');
  return {
    getSummary: () => call('/usage/summary'),
    getCostAlert: () => call('/cost-alerts'),
    setCostAlert: (body) => call('/cost-alerts', { method: 'PUT', body: JSON.stringify(body) }),
    getCostAlertHistory: () => call('/cost-alerts/history'),
    slackTest: (body) => call('/cost-alerts/slack-test', { method: 'POST', body: JSON.stringify(body) }),
    listWorkspaces: () => call('/feature-flags/workspaces'),
    getBudgetAlert: (wsId) => wsCall(`/workspaces/${encodeURIComponent(wsId)}/budget-alert`),
    setBudgetAlert: (wsId, body) => wsCall(`/workspaces/${encodeURIComponent(wsId)}/budget-alert`, { method: 'PUT', body: JSON.stringify(body) }),
  };
}

function featureFlagsApi(token) {
  const call = makeCall(token, '/api/mgmt/v1/admin');
  return {
    getCatalog: () => call('/feature-flags/catalog'),
    listWorkspaces: () => call('/feature-flags/workspaces'),
    getFlags: (wsId) => call(`/feature-flags?workspace_id=${encodeURIComponent(wsId)}`),
    setFlags: (wsId, flags) => call('/feature-flags', { method: 'PUT', body: JSON.stringify({ workspace_id: wsId, flags }) }),
  };
}

function Spinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400" data-testid="embed-admin-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[var(--embed-primary,#6366f1)]" />
      {label ? <p className="text-xs">{label}</p> : null}
    </div>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div className="m-4 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="embed-admin-error">
      <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-rose-200">Couldn&apos;t load this panel</p>
        <p className="text-xs text-rose-300/80 mt-1">{message}</p>
        {onRetry ? (
          <button onClick={onRetry} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-rose-200 hover:text-white" data-testid="embed-admin-retry">
            <RefreshCw className="w-3.5 h-3.5" /> Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Toggle({ on, onClick, testId, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-600'} ${disabled ? 'opacity-50' : ''}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

function UserManagementPanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', users: [], roles: [] });
  const [savingId, setSavingId] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [u, r] = await Promise.all([api.listUsers(), api.listRoles()]);
      setState({ loading: false, error: '', users: u.data || [], roles: r.data || [] });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (userId, role) => {
    setSavingId(userId);
    try {
      await api.setUserRole(userId, role);
      setState((s) => ({ ...s, users: s.users.map((x) => (x.user_id === userId ? { ...x, role } : x)) }));
    } catch (e) {
      setState((s) => ({ ...s, error: e.message }));
    } finally {
      setSavingId('');
    }
  };

  if (state.loading) return <Spinner label="Loading users…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;

  const roleNames = state.roles.map((r) => r.role_name);

  return (
    <div className="p-4" data-testid="embed-admin-user-management">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400">{state.users.length} user{state.users.length === 1 ? '' : 's'}</p>
        <button onClick={load} className="text-slate-400 hover:text-white" data-testid="embed-admin-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {state.users.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5" data-testid={`embed-admin-user-${u.user_id}`}>
            <div className="w-8 h-8 rounded-full bg-[var(--embed-primary,#6366f1)]/20 flex items-center justify-center text-[11px] font-bold text-[var(--embed-primary,#818cf8)]">
              {(u.name || u.email || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{u.name || u.email}</p>
              <p className="text-[11px] text-slate-400 truncate">{u.email}</p>
            </div>
            <select
              value={roleNames.includes(u.role) ? u.role : ''}
              onChange={(e) => changeRole(u.user_id, e.target.value)}
              disabled={savingId === u.user_id}
              data-testid={`embed-admin-role-select-${u.user_id}`}
              className="text-xs bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-[var(--embed-primary,#6366f1)]"
            >
              {!roleNames.includes(u.role) ? <option value="" disabled>{u.role || 'select role'}</option> : null}
              {state.roles.map((r, i) => (
                <option key={`${r.role_name}-${i}`} value={r.role_name}>{r.role_name}{r.is_system ? '' : ' (custom)'}</option>
              ))}
            </select>
            {savingId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function GovSection({ icon: Icon, title, count, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[var(--embed-primary,#818cf8)]" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-300">{title}</h3>
        <span className="text-[10px] text-slate-500">({count})</span>
      </div>
      {children}
    </div>
  );
}

function GovernancePanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', rls: [], masking: [], certs: [] });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [rls, masking, certs] = await Promise.all([api.listRls(), api.listMasking(), api.listCertifications()]);
      setState({ loading: false, error: '', rls: rls.data || [], masking: masking.data || [], certs: certs.data || [] });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleRls = async (id) => {
    try {
      const r = await api.toggleRls(id);
      setState((s) => ({ ...s, rls: s.rls.map((p) => (p.policy_id === id ? { ...p, enabled: r.enabled } : p)) }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const toggleMasking = async (id) => {
    try {
      const r = await api.toggleMasking(id);
      setState((s) => ({ ...s, masking: s.masking.map((p) => (p.policy_id === id ? { ...p, enabled: r.enabled } : p)) }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };

  if (state.loading) return <Spinner label="Loading governance…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;

  return (
    <div className="p-4" data-testid="embed-admin-governance">
      <div className="flex justify-end mb-2">
        <button onClick={load} className="text-slate-400 hover:text-white" data-testid="embed-admin-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      <GovSection icon={Lock} title="Row-level security" count={state.rls.length}>
        {state.rls.length === 0 ? <p className="text-xs text-slate-500">No RLS policies.</p> : state.rls.map((p) => (
          <div key={p.policy_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-1.5" data-testid={`embed-admin-rls-${p.policy_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{p.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{p.resource_type} · {p.scope}{p.role ? ` · ${p.role}` : ''}</p>
            </div>
            <Toggle on={!!p.enabled} onClick={() => toggleRls(p.policy_id)} testId={`embed-admin-rls-toggle-${p.policy_id}`} />
          </div>
        ))}
      </GovSection>

      <GovSection icon={Eye} title="Column masking" count={state.masking.length}>
        {state.masking.length === 0 ? <p className="text-xs text-slate-500">No masking policies.</p> : state.masking.map((p) => (
          <div key={p.policy_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-1.5" data-testid={`embed-admin-mask-${p.policy_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{p.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{(p.columns || []).join(', ')} · {p.strategy}</p>
            </div>
            <Toggle on={!!p.enabled} onClick={() => toggleMasking(p.policy_id)} testId={`embed-admin-mask-toggle-${p.policy_id}`} />
          </div>
        ))}
      </GovSection>

      <GovSection icon={BadgeCheck} title="Certifications" count={state.certs.length}>
        {state.certs.length === 0 ? <p className="text-xs text-slate-500">No certified content yet.</p> : state.certs.map((c) => (
          <div key={`${c.resource_type}-${c.resource_id}`} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-1.5">
            <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${c.level === 'certified' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : c.level === 'deprecated' ? 'text-rose-300 border-rose-500/40 bg-rose-500/10' : 'text-amber-300 border-amber-500/40 bg-amber-500/10'}`}>{c.level}</span>
            <p className="text-sm text-white truncate flex-1">{c.resource_type} · {c.resource_id}</p>
          </div>
        ))}
      </GovSection>
    </div>
  );
}

function SecretReveal({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const t = document.createElement('textarea');
      t.value = value; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(t);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 mt-2" data-testid={testId}>
      <p className="text-[11px] text-amber-200/90 mb-1.5">{label} — copy it now, it won&apos;t be shown again.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[11px] text-amber-100 break-all bg-black/30 rounded px-2 py-1.5">{value}</code>
        <button onClick={copy} className="p-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100" data-testid={`${testId}-copy`}>
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

const inputCls = 'text-xs bg-slate-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-[var(--embed-primary,#6366f1)]';
const btnCls = 'inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 bg-[var(--embed-primary,#6366f1)]/20 text-[var(--embed-primary,#818cf8)] hover:bg-[var(--embed-primary,#6366f1)]/30 disabled:opacity-50';

const IDENTITY_ROLES = ['viewer', 'analyst', 'editor', 'data_steward', 'admin', 'super_admin'];

function IdentityPanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', cfg: null, tokens: [], mappings: [] });
  const [tokenName, setTokenName] = useState('');
  const [reveal, setReveal] = useState('');
  const [busy, setBusy] = useState('');
  const [mapGroup, setMapGroup] = useState('');
  const [mapRole, setMapRole] = useState('viewer');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [cfg, tks, maps] = await Promise.all([api.getConfig(), api.listTokens(), api.listMappings()]);
      setState({ loading: false, error: '', cfg, tokens: tks.data || [], mappings: maps.data || [] });
    } catch (e) { setState((s) => ({ ...s, loading: false, error: e.message })); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    setBusy('cfg');
    try {
      const cfg = await api.setConfig({ ...{ mode: state.cfg?.mode, default_role: state.cfg?.default_role }, ...patch });
      setState((s) => ({ ...s, cfg }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); } finally { setBusy(''); }
  };
  const createToken = async () => {
    setBusy('token');
    try {
      const r = await api.createToken(tokenName || 'SCIM token');
      setReveal(r.token); setTokenName('');
      // Re-sync from the server so the list (and revoke ids) are authoritative,
      // without flipping the loading flag (which would hide the reveal).
      const tks = await api.listTokens();
      setState((s) => ({ ...s, tokens: tks.data || [] }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); } finally { setBusy(''); }
  };
  const revokeToken = async (id) => {
    try { await api.revokeToken(id); setState((s) => ({ ...s, tokens: s.tokens.filter((t) => t.token_id !== id) })); }
    catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const addMapping = async () => {
    if (!mapGroup.trim()) return;
    setBusy('map');
    try {
      const row = await api.setMapping(mapGroup.trim(), mapRole);
      setState((s) => ({ ...s, mappings: [...s.mappings.filter((m) => m.group_key !== row.group_key), row] }));
      setMapGroup('');
    } catch (e) { setState((s) => ({ ...s, error: e.message })); } finally { setBusy(''); }
  };
  const deleteMapping = async (group) => {
    try { await api.deleteMapping(group); setState((s) => ({ ...s, mappings: s.mappings.filter((m) => (m.group || m.group_key) !== group) })); }
    catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };

  if (state.loading) return <Spinner label="Loading identity…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;

  const roleNames = IDENTITY_ROLES;
  const mode = state.cfg?.mode || 'internal';

  return (
    <div className="p-4" data-testid="embed-admin-identity">
      <GovSection icon={Fingerprint} title="Identity mode" count={mode}>
        <div className="flex items-center gap-2 mb-2" data-testid="identity-mode-toggle">
          {['internal', 'federated'].map((m) => (
            <button key={m} onClick={() => saveConfig({ mode: m })} disabled={busy === 'cfg'}
              data-testid={`identity-mode-${m}`}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${mode === m ? 'bg-[var(--embed-primary,#6366f1)] text-white border-transparent' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}>
              {m === 'internal' ? 'INZARA-managed' : 'Directory-managed'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">Default role for new users</span>
          <select value={state.cfg?.default_role || 'viewer'} onChange={(e) => saveConfig({ default_role: e.target.value })}
            disabled={busy === 'cfg'} data-testid="identity-default-role" className={inputCls}>
            {IDENTITY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </GovSection>

      <GovSection icon={KeyRound} title="SCIM tokens" count={state.tokens.length}>
        <div className="flex items-center gap-2 mb-2">
          <input value={tokenName} onChange={(e) => setTokenName(e.target.value)} placeholder="Token name (e.g. Okta prod)"
            className={`${inputCls} flex-1`} data-testid="identity-token-name" />
          <button onClick={createToken} disabled={busy === 'token'} className={btnCls} data-testid="identity-token-create">
            <Plus className="w-3.5 h-3.5" /> Generate
          </button>
        </div>
        {reveal ? <SecretReveal label="SCIM bearer token" value={reveal} testId="identity-token-reveal" /> : null}
        <div className="space-y-1.5 mt-2">
          {state.tokens.length === 0 ? <p className="text-xs text-slate-500">No SCIM tokens yet.</p> : state.tokens.map((t) => (
            <div key={t.token_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`identity-token-${t.token_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{t.name}</p>
                <p className="text-[11px] text-slate-400 truncate font-mono">{t.prefix}…</p>
              </div>
              <button onClick={() => revokeToken(t.token_id)} className="text-slate-400 hover:text-rose-400" data-testid={`identity-token-revoke-${t.token_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </GovSection>

      <GovSection icon={ShieldCheck} title="Group → role mappings" count={state.mappings.length}>
        <div className="flex items-center gap-2 mb-2">
          <input value={mapGroup} onChange={(e) => setMapGroup(e.target.value)} placeholder="IdP group name"
            className={`${inputCls} flex-1`} data-testid="identity-map-group" />
          <select value={mapRole} onChange={(e) => setMapRole(e.target.value)} className={inputCls} data-testid="identity-map-role">
            {roleNames.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={addMapping} disabled={busy === 'map'} className={btnCls} data-testid="identity-map-add">
            <Plus className="w-3.5 h-3.5" /> Map
          </button>
        </div>
        <div className="space-y-1.5">
          {state.mappings.length === 0 ? <p className="text-xs text-slate-500">No group mappings — unmapped groups get the default role.</p> : state.mappings.map((m) => (
            <div key={m.group_key} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`identity-map-${m.group_key}`}>
              <p className="text-sm text-white truncate flex-1">{m.group || m.group_key}</p>
              <span className="text-[11px] font-semibold text-[var(--embed-primary,#818cf8)]">{m.role}</span>
              <button onClick={() => deleteMapping(m.group || m.group_key)} className="text-slate-400 hover:text-rose-400" data-testid={`identity-map-delete-${m.group_key}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </GovSection>
    </div>
  );
}

const DELIVERY_STATUS_CLS = {
  success: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  pending: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  failed: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
};

function WebhooksPanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', endpoints: [], events: [], deliveries: [] });
  const [newUrl, setNewUrl] = useState('');
  const [selEvents, setSelEvents] = useState([]);
  const [reveal, setReveal] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [eps, ev, del] = await Promise.all([api.listEndpoints(), api.listEvents(), api.listDeliveries()]);
      setState({ loading: false, error: '', endpoints: eps.data || [], events: ev.events || [], deliveries: del.data || [] });
    } catch (e) { setState((s) => ({ ...s, loading: false, error: e.message })); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const refreshDeliveries = async () => {
    try { const del = await api.listDeliveries(); setState((s) => ({ ...s, deliveries: del.data || [] })); } catch { /* noop */ }
  };
  const create = async () => {
    if (!newUrl.trim()) return;
    setBusy('create');
    try {
      const events = selEvents.length ? selEvents : undefined;
      const ep = await api.createEndpoint({ url: newUrl.trim(), events });
      setReveal(ep.secret); setNewUrl(''); setSelEvents([]);
      setState((s) => ({ ...s, endpoints: [{ ...ep }, ...s.endpoints] }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); } finally { setBusy(''); }
  };
  const toggleEvent = (ev) => setSelEvents((s) => (s.includes(ev) ? s.filter((x) => x !== ev) : [...s, ev]));
  const toggleEnabled = async (ep) => {
    try {
      const upd = await api.updateEndpoint(ep.endpoint_id, { enabled: !ep.enabled });
      setState((s) => ({ ...s, endpoints: s.endpoints.map((x) => (x.endpoint_id === ep.endpoint_id ? { ...x, enabled: upd.enabled } : x)) }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const rotate = async (id) => {
    try { const ep = await api.rotateSecret(id); setReveal(ep.secret); } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const del = async (id) => {
    try { await api.deleteEndpoint(id); setState((s) => ({ ...s, endpoints: s.endpoints.filter((x) => x.endpoint_id !== id) })); }
    catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const test = async (id) => {
    try { await api.testEndpoint(id); await refreshDeliveries(); } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };
  const replay = async (id) => {
    try { await api.replayDelivery(id); await refreshDeliveries(); } catch (e) { setState((s) => ({ ...s, error: e.message })); }
  };

  if (state.loading) return <Spinner label="Loading webhooks…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;

  return (
    <div className="p-4" data-testid="embed-admin-webhooks">
      <GovSection icon={Webhook} title="Endpoints" count={state.endpoints.length}>
        <div className="flex items-center gap-2 mb-2">
          <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://your-app.com/inzara-webhook"
            className={`${inputCls} flex-1`} data-testid="webhook-new-url" />
          <button onClick={create} disabled={busy === 'create'} className={btnCls} data-testid="webhook-create">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="mb-2" data-testid="webhook-event-picker">
          <p className="text-[11px] text-slate-400 mb-1.5">Events {selEvents.length === 0 ? '(none selected = all events)' : `(${selEvents.length} selected)`}</p>
          <div className="flex flex-wrap gap-1.5">
            {state.events.map((ev) => {
              const on = selEvents.includes(ev);
              return (
                <button key={ev} onClick={() => toggleEvent(ev)} data-testid={`webhook-event-chip-${ev}`}
                  className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${on ? 'bg-[var(--embed-primary,#6366f1)] text-white border-transparent' : 'border-white/15 text-slate-300 hover:bg-white/5'}`}>
                  {on ? <Check className="w-3 h-3 inline mr-1" /> : null}{ev}
                </button>
              );
            })}
          </div>
        </div>
        {reveal ? <SecretReveal label="Signing secret" value={reveal} testId="webhook-secret-reveal" /> : null}
        <div className="space-y-1.5 mt-2">
          {state.endpoints.length === 0 ? <p className="text-xs text-slate-500">No endpoints yet.</p> : state.endpoints.map((ep) => (
            <div key={ep.endpoint_id} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`webhook-ep-${ep.endpoint_id}`}>
              <div className="flex items-center gap-2">
                <p className="text-sm text-white truncate flex-1 font-mono">{ep.url}</p>
                <Toggle on={!!ep.enabled} onClick={() => toggleEnabled(ep)} testId={`webhook-ep-toggle-${ep.endpoint_id}`} />
                <button onClick={() => rotate(ep.endpoint_id)} title="Rotate secret" className="text-slate-400 hover:text-white" data-testid={`webhook-ep-rotate-${ep.endpoint_id}`}><RotateCw className="w-3.5 h-3.5" /></button>
                <button onClick={() => test(ep.endpoint_id)} title="Send test" className="text-slate-400 hover:text-white" data-testid={`webhook-ep-test-${ep.endpoint_id}`}><Send className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(ep.endpoint_id)} title="Delete" className="text-slate-400 hover:text-rose-400" data-testid={`webhook-ep-delete-${ep.endpoint_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 truncate">{(ep.events || []).join(', ')}</p>
            </div>
          ))}
        </div>
      </GovSection>

      <GovSection icon={Power} title="Recent deliveries" count={state.deliveries.length}>
        <div className="flex justify-end mb-1">
          <button onClick={refreshDeliveries} className="text-slate-400 hover:text-white" data-testid="webhook-deliveries-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
        {state.deliveries.length === 0 ? <p className="text-xs text-slate-500">No deliveries yet.</p> : state.deliveries.map((d) => (
          <div key={d.delivery_id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-1.5" data-testid={`webhook-delivery-${d.delivery_id}`}>
            <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${DELIVERY_STATUS_CLS[d.status] || 'text-slate-300 border-white/20'}`}>{d.status}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-white truncate">{d.event_type}</p>
              <p className="text-[10px] text-slate-500 truncate">attempt {d.attempts}{d.last_status_code ? ` · HTTP ${d.last_status_code}` : ''} · {String(d.created_at || '').slice(0, 19).replace('T', ' ')}</p>
            </div>
            <button onClick={() => replay(d.delivery_id)} title="Replay" className="text-slate-400 hover:text-white" data-testid={`webhook-delivery-replay-${d.delivery_id}`}><RotateCw className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </GovSection>
    </div>
  );
}

function QuotaBar({ label, used, limit }) {
  const unlimited = limit == null || limit < 0;
  const pct = unlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const danger = !unlimited && pct >= 90;
  return (
    <div className="mb-2.5" data-testid={`usage-quota-${label}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-400 flex items-center gap-1">
          {used}{unlimited ? <> / <InfinityIcon className="w-3 h-3 inline" /></> : ` / ${limit}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full ${danger ? 'bg-rose-400' : 'bg-[var(--embed-primary,#818cf8)]'}`}
          style={{ width: `${unlimited ? 6 : pct}%` }} />
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="flex-1 min-w-[120px] rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
      <p className="text-lg font-semibold text-white">{value}</p>
    </div>
  );
}

function UsagePanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [alert, setAlert] = useState({ enabled: false, thresholds: [], slack_configured: false });
  const [slackUrl, setSlackUrl] = useState('');
  const [slackDirty, setSlackDirty] = useState(false);
  const [slackMsg, setSlackMsg] = useState('');
  const [history, setHistory] = useState([]);
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [summary, ca, hist] = await Promise.all([
        api.getSummary(),
        api.getCostAlert().catch(() => null),
        api.getCostAlertHistory().catch(() => null),
      ]);
      setState({ loading: false, error: '', data: summary });
      if (ca) {
        const thresholds = (ca.thresholds && ca.thresholds.length)
          ? ca.thresholds
          : (ca.threshold_usd ? [{ tier: 'exceeded', usd: ca.threshold_usd }] : []);
        setAlert({ enabled: !!ca.enabled, thresholds, slack_configured: !!ca.slack_configured });
      }
      if (hist) setHistory(hist.data || []);
    } catch (e) { setState((s) => ({ ...s, loading: false, error: e.message })); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setTier = (i, patch) => setAlert((a) => ({ ...a, thresholds: a.thresholds.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const addTier = () => setAlert((a) => ({ ...a, thresholds: [...a.thresholds, { tier: a.thresholds.length ? 'critical' : 'warning', usd: '' }] }));
  const removeTier = (i) => setAlert((a) => ({ ...a, thresholds: a.thresholds.filter((_, j) => j !== i) }));

  const cleanThresholds = () => alert.thresholds
    .filter((t) => t.usd !== '' && Number(t.usd) > 0)
    .map((t) => ({ tier: (t.tier || 'exceeded').trim() || 'exceeded', usd: Number(t.usd) }));

  const saveAlert = async () => {
    setSavingAlert(true); setAlertMsg('');
    try {
      const body = { enabled: alert.enabled, thresholds: cleanThresholds() };
      if (slackDirty) body.slack_webhook_url = slackUrl.trim();
      const r = await api.setCostAlert(body);
      setAlert({ enabled: !!r.enabled, thresholds: r.thresholds || [], slack_configured: !!r.slack_configured });
      setSlackUrl(''); setSlackDirty(false);
      setAlertMsg('Saved');
      api.getCostAlertHistory().then((h) => setHistory(h.data || [])).catch(() => {});
    } catch (e) { setAlertMsg(e.message); } finally { setSavingAlert(false); }
  };

  const testSlack = async () => {
    setSlackMsg('Sending…');
    try {
      const r = await api.slackTest(slackUrl.trim() ? { slack_webhook_url: slackUrl.trim() } : {});
      setSlackMsg(r.ok ? 'Test message sent ✓' : `Failed: ${r.error || r.status_code || 'unknown'}`);
    } catch (e) { setSlackMsg(e.message); }
  };

  const clearSlack = async () => {
    setSlackMsg('');
    try {
      const r = await api.setCostAlert({ enabled: alert.enabled, thresholds: cleanThresholds(), slack_webhook_url: '' });
      setAlert({ enabled: !!r.enabled, thresholds: r.thresholds || [], slack_configured: !!r.slack_configured });
      setSlackUrl(''); setSlackDirty(false); setSlackMsg('Slack cleared');
    } catch (e) { setSlackMsg(e.message); }
  };

  if (state.loading) return <Spinner label="Loading usage…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;

  const d = state.data;
  const cm = d.current_month || {};
  const daily = d.daily || [];
  const maxQ = Math.max(1, ...daily.map((x) => x.queries || 0));

  return (
    <div className="p-4" data-testid="embed-admin-usage">
      <GovSection icon={BadgeCheck} title="Plan" count={d.plan?.name}>
        <div className="flex flex-wrap gap-2" data-testid="usage-plan">
          <Tile icon={BadgeCheck} label="Plan" value={d.plan?.name} />
          <Tile icon={Users} label="Seats" value={d.plan?.seats_used} />
          <Tile icon={Gauge} label="Status" value={d.plan?.status} />
          <Tile icon={DollarSign} label="Per seat / mo" value={`$${d.plan?.price_per_user ?? 0}`} />
        </div>
        {d.plan?.current_period_end ? (
          <p className="text-[11px] text-slate-500 mt-2">Renews {String(d.plan.current_period_end).slice(0, 10)}</p>
        ) : null}
      </GovSection>

      <GovSection icon={DollarSign} title="This month" count={`$${cm.cost_estimate_usd ?? 0}`}>
        <div className="flex flex-wrap gap-2" data-testid="usage-current-month">
          <Tile icon={BarChart3} label="Queries" value={cm.queries ?? 0} />
          <Tile icon={BarChart3} label="AI queries" value={cm.ai_queries ?? 0} />
          <Tile icon={RefreshCw} label="Cache hits" value={cm.cache_hits ?? 0} />
          <Tile icon={DollarSign} label="Est. cost" value={`$${cm.cost_estimate_usd ?? 0}`} />
        </div>
      </GovSection>

      <GovSection icon={DollarSign} title="Cost alerts" count={alert.enabled ? `${alert.thresholds.length} tier${alert.thresholds.length === 1 ? '' : 's'}` : 'off'}>
        <div className="flex items-center gap-3 mb-2" data-testid="usage-cost-alert">
          <Toggle on={alert.enabled} onClick={() => setAlert((a) => ({ ...a, enabled: !a.enabled }))} testId="cost-alert-toggle" />
          <span className="text-[11px] text-slate-400">Notify (webhook{alert.slack_configured || slackUrl ? ' + Slack' : ''}) when this month passes a tier</span>
        </div>

        <div className="space-y-1.5" data-testid="cost-alert-thresholds">
          {alert.thresholds.length === 0 ? <p className="text-[11px] text-slate-500">No thresholds — add one below.</p> : null}
          {alert.thresholds.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`cost-alert-tier-${i}`}>
              <input value={t.tier} onChange={(e) => setTier(i, { tier: e.target.value })}
                placeholder="warning" className={`${inputCls} w-28`} data-testid={`cost-alert-tier-name-${i}`} />
              <span className="text-slate-400 text-xs">$</span>
              <input type="number" min="0" value={t.usd} onChange={(e) => setTier(i, { usd: e.target.value })}
                placeholder="100" className={`${inputCls} w-24`} data-testid={`cost-alert-tier-amount-${i}`} />
              <button onClick={() => removeTier(i)} className="text-slate-400 hover:text-rose-400" data-testid={`cost-alert-tier-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={addTier} className="text-[11px] text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="cost-alert-add-tier">
            <Plus className="w-3 h-3" /> Add threshold
          </button>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <span className="text-[11px] text-slate-400 w-20">Slack</span>
          <input value={slackUrl} onChange={(e) => { setSlackUrl(e.target.value); setSlackDirty(true); }}
            placeholder={alert.slack_configured ? 'configured — paste to replace' : 'https://hooks.slack.com/services/…'}
            className={`${inputCls} flex-1`} data-testid="cost-alert-slack-url" />
          <button onClick={testSlack} className={btnCls} data-testid="cost-alert-slack-test">
            <Send className="w-3.5 h-3.5" /> Test
          </button>
          {(alert.slack_configured || slackUrl) ? (
            <button onClick={clearSlack} className="text-slate-400 hover:text-rose-400 text-[11px] inline-flex items-center gap-1" data-testid="cost-alert-slack-clear">
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          ) : null}
        </div>
        {slackMsg ? <p className="text-[11px] text-slate-400 mt-1 ml-20" data-testid="cost-alert-slack-msg">{slackMsg}</p> : null}

        <div className="flex items-center gap-3 mt-3">
          <button onClick={saveAlert} disabled={savingAlert} className={btnCls} data-testid="cost-alert-save">Save</button>
          {alertMsg ? <span className="text-[11px] text-slate-400" data-testid="cost-alert-msg">{alertMsg}</span> : null}
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">Each tier fires the <code>tenant.cost_threshold_exceeded</code> webhook (+ Slack if set) once per month.</p>

        {history.length > 0 ? (
          <div className="mt-3" data-testid="cost-alert-history">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">Recent alerts</p>
            <div className="space-y-1">
              {history.slice(0, 8).map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-slate-300" data-testid={`cost-alert-history-row-${i}`}>
                  <span className="px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10">{h.tier}</span>
                  <span>${Number(h.month_to_date_cost_usd).toFixed(2)} ≥ ${Number(h.threshold_usd).toFixed(2)}</span>
                  <span className="text-slate-500">· {h.period}</span>
                  <span className="ml-auto text-slate-500">{String(h.fired_at).slice(0, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </GovSection>

      <WorkspaceBudgetAlerts api={api} />

      <GovSection icon={Gauge} title="Resource usage vs plan" count={(d.resource_usage || []).length}>
        {(d.resource_usage || []).length === 0 ? <p className="text-xs text-slate-500">No limits on this plan.</p>
          : (d.resource_usage || []).map((r) => <QuotaBar key={r.key} label={r.label} used={r.used} limit={r.limit} />)}
      </GovSection>

      <GovSection icon={BarChart3} title="Queries — last 30 days" count={cm.queries ?? 0}>
        {daily.length === 0 ? <p className="text-xs text-slate-500">No query activity yet.</p> : (
          <div className="flex items-end gap-[3px] h-24 mt-1" data-testid="usage-trend">
            {daily.map((x) => (
              <div key={x.day} title={`${x.day}: ${x.queries} queries`}
                className="flex-1 rounded-t bg-[var(--embed-primary,#818cf8)] opacity-70 hover:opacity-100 transition-opacity"
                style={{ height: `${Math.max(2, Math.round(((x.queries || 0) / maxQ) * 100))}%` }} />
            ))}
          </div>
        )}
      </GovSection>
    </div>
  );
}

function WorkspaceBudgetAlerts({ api }) {
  const [state, setState] = useState({ loading: true, error: '', workspaces: [], wsId: '' });
  const [cfg, setCfg] = useState({ enabled: false, threshold_usd: '', recipients: '', history: [] });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadCfg = useCallback(async (wsId) => {
    const r = await api.getBudgetAlert(wsId);
    setCfg({
      enabled: !!r.enabled,
      threshold_usd: r.threshold_usd ?? '',
      recipients: (r.recipients || []).join(', '),
      history: r.history || [],
      mtd: r.month_to_date_cost_usd ?? 0,
      progress: r.progress_pct,
    });
  }, [api]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const ws = await api.listWorkspaces();
      const list = ws.data || [];
      if (list.length === 0) { setState({ loading: false, error: '', workspaces: [], wsId: '' }); return; }
      const first = list[0].workspace_id;
      await loadCfg(first);
      setState({ loading: false, error: '', workspaces: list, wsId: first });
    } catch (e) { setState((s) => ({ ...s, loading: false, error: e.message })); }
  }, [api, loadCfg]);
  useEffect(() => { load(); }, [load]);

  const switchWs = async (wsId) => {
    setState((s) => ({ ...s, wsId })); setMsg('');
    try { await loadCfg(wsId); } catch (e) { setMsg(e.message); }
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const recipients = cfg.recipients.split(/[,\s]+/).map((r) => r.trim()).filter(Boolean);
      const body = { enabled: cfg.enabled, recipients };
      if (cfg.threshold_usd !== '' && Number(cfg.threshold_usd) > 0) body.threshold_usd = Number(cfg.threshold_usd);
      const r = await api.setBudgetAlert(state.wsId, body);
      setCfg((c) => ({ ...c, enabled: !!r.enabled, threshold_usd: r.threshold_usd ?? '', recipients: (r.recipients || []).join(', ') }));
      await loadCfg(state.wsId);
      setMsg('Saved');
    } catch (e) { setMsg(e.message); } finally { setSaving(false); }
  };

  if (state.loading) return <GovSection icon={DollarSign} title="Workspace budget alerts" count="…"><Spinner label="Loading budget alerts…" /></GovSection>;
  if (state.error) return <GovSection icon={DollarSign} title="Workspace budget alerts" count="!"><ErrorBox message={state.error} onRetry={load} /></GovSection>;
  if (state.workspaces.length === 0) {
    return <GovSection icon={DollarSign} title="Workspace budget alerts" count="0"><p className="text-[11px] text-slate-500" data-testid="wba-no-workspaces">No workspaces found for this tenant.</p></GovSection>;
  }

  return (
    <GovSection icon={DollarSign} title="Workspace budget alerts" count={cfg.enabled ? `$${cfg.threshold_usd || 0}/mo` : 'off'}>
      <div data-testid="embed-admin-workspace-budget">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] text-slate-400">Workspace</span>
          <select value={state.wsId} onChange={(e) => switchWs(e.target.value)} className={inputCls} data-testid="wba-workspace-select">
            {state.workspaces.map((w) => <option key={w.workspace_id} value={w.workspace_id}>{w.name || w.workspace_id}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <Toggle on={cfg.enabled} onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))} testId="wba-toggle" />
          <span className="text-[11px] text-slate-400">Email recipients once this workspace passes its monthly budget</span>
        </div>

        {Number(cfg.threshold_usd) > 0 ? (
          (() => {
            const pct = cfg.progress != null ? cfg.progress : Math.min(100, (Number(cfg.mtd || 0) / Number(cfg.threshold_usd)) * 100);
            const over = Number(cfg.mtd || 0) >= Number(cfg.threshold_usd);
            const barColor = over ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <div className="mb-3" data-testid="wba-progress">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-slate-400">This month</span>
                  <span className={`text-[11px] font-semibold ${over ? 'text-red-400' : 'text-slate-200'}`} data-testid="wba-progress-label">
                    ${Number(cfg.mtd || 0).toFixed(2)} / ${Number(cfg.threshold_usd).toFixed(2)} ({pct.toFixed(0)}%)
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
                </div>
              </div>
            );
          })()
        ) : null}

        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] text-slate-400 w-24">Monthly budget</span>
          <span className="text-slate-400 text-xs">$</span>
          <input type="number" min="0" value={cfg.threshold_usd}
            onChange={(e) => setCfg((c) => ({ ...c, threshold_usd: e.target.value }))}
            placeholder="500" className={`${inputCls} w-28`} data-testid="wba-threshold" />
        </div>

        <div className="flex items-start gap-2 mb-3">
          <span className="text-[11px] text-slate-400 w-24 pt-1.5">Recipients</span>
          <input value={cfg.recipients}
            onChange={(e) => setCfg((c) => ({ ...c, recipients: e.target.value }))}
            placeholder="ops@you.com, billing@you.com (blank = tenant alert recipients)"
            className={`${inputCls} flex-1`} data-testid="wba-recipients" />
        </div>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className={btnCls} data-testid="wba-save">Save</button>
          {msg ? <span className="text-[11px] text-slate-400" data-testid="wba-msg">{msg}</span> : null}
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">Fires once per calendar month per workspace. Uses your configured email provider.</p>

        {cfg.history.length > 0 ? (
          <div className="mt-3" data-testid="wba-history">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">Recent alerts</p>
            <div className="space-y-1">
              {cfg.history.slice(0, 6).map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-slate-300" data-testid={`wba-history-row-${i}`}>
                  <span className="px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10">{h.period}</span>
                  <span>${Number(h.month_to_date_cost_usd).toFixed(2)} ≥ ${Number(h.threshold_usd).toFixed(2)}</span>
                  <span className="text-slate-500">· {h.recipients_notified} emailed</span>
                  <span className="ml-auto text-slate-500">{String(h.fired_at).slice(0, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </GovSection>
  );
}

function FeatureFlagsPanel({ api }) {
  const [state, setState] = useState({ loading: true, error: '', workspaces: [], wsId: '', flags: {} });
  const [saving, setSaving] = useState('');

  const loadFlags = useCallback(async (wsId) => {
    const r = await api.getFlags(wsId);
    setState((s) => ({ ...s, wsId, flags: r.flags || {} }));
  }, [api]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const ws = await api.listWorkspaces();
      const list = ws.data || [];
      if (list.length === 0) { setState({ loading: false, error: '', workspaces: [], wsId: '', flags: {} }); return; }
      const first = list[0].workspace_id;
      const r = await api.getFlags(first);
      setState({ loading: false, error: '', workspaces: list, wsId: first, flags: r.flags || {} });
    } catch (e) { setState((s) => ({ ...s, loading: false, error: e.message })); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (flag) => {
    const next = !state.flags[flag]?.enabled;
    setSaving(flag);
    try {
      const r = await api.setFlags(state.wsId, { [flag]: next });
      setState((s) => ({ ...s, flags: r.flags || s.flags }));
    } catch (e) { setState((s) => ({ ...s, error: e.message })); } finally { setSaving(''); }
  };

  if (state.loading) return <Spinner label="Loading feature flags…" />;
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />;
  if (state.workspaces.length === 0) {
    return <div className="p-4"><p className="text-xs text-slate-400" data-testid="ff-no-workspaces">No workspaces found for this tenant. Create a workspace to configure feature flags.</p></div>;
  }

  const flags = state.flags;
  const categories = [...new Set(Object.values(flags).map((f) => f.category))];

  return (
    <div className="p-4" data-testid="embed-admin-feature-flags">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] text-slate-400">Workspace</span>
        <select value={state.wsId} onChange={(e) => loadFlags(e.target.value)} className={inputCls} data-testid="ff-workspace-select">
          {state.workspaces.map((w) => <option key={w.workspace_id} value={w.workspace_id}>{w.name || w.workspace_id}</option>)}
        </select>
      </div>
      {categories.map((cat) => (
        <GovSection key={cat} icon={SlidersHorizontal} title={cat} count={Object.values(flags).filter((f) => f.category === cat).length}>
          {Object.entries(flags).filter(([, f]) => f.category === cat).map(([flag, f]) => (
            <div key={flag} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-1.5" data-testid={`ff-row-${flag}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{flag}</p>
                <p className="text-[11px] text-slate-400 truncate">{f.description}{f.source !== 'default' ? ` · ${f.source} override` : ''}</p>
              </div>
              <Toggle on={!!f.enabled} onClick={() => toggle(flag)} testId={`ff-toggle-${flag}`} disabled={saving === flag} />
            </div>
          ))}
        </GovSection>
      ))}
    </div>
  );
}

export default function EmbedAdminPanelPage() {
  const { panel } = useParams();
  const token = useEmbedToken();
  const api = useMemo(() => {
    if (panel === 'identity') return identityApi(token);
    if (panel === 'webhooks') return webhooksApi(token);
    if (panel === 'usage') return usageApi(token);
    if (panel === 'feature-flags') return featureFlagsApi(token);
    return adminApi(token);
  }, [panel, token]);
  const branding = null; // future: fetch tenant branding via token

  const meta = {
    governance: { icon: ShieldCheck, title: 'Governance' },
    identity: { icon: Fingerprint, title: 'Identity Bridge' },
    webhooks: { icon: Webhook, title: 'Webhooks' },
    usage: { icon: BarChart3, title: 'Usage & Billing' },
    'feature-flags': { icon: SlidersHorizontal, title: 'Feature Flags' },
  }[panel] || { icon: Users, title: 'User Management' };

  if (!token) {
    return (
      <div style={brandingRootStyle(branding)} className="min-h-screen bg-slate-950 text-white">
        <Spinner label="Waiting for admin session token…" />
      </div>
    );
  }

  const Icon = meta.icon;
  return (
    <div style={brandingRootStyle(branding)} className="min-h-screen bg-slate-950 text-white" data-testid="embed-admin-panel">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-white/10 bg-slate-950/80 backdrop-blur px-4 py-3">
        <Icon className="w-4 h-4 text-[var(--embed-primary,#818cf8)]" />
        <h1 className="text-sm font-semibold">{meta.title}</h1>
        <span className="ml-auto text-[10px] text-slate-500">{brandingProductName(branding, 'INZARA')} Admin</span>
      </header>
      {panel === 'governance' ? <GovernancePanel api={api} />
        : panel === 'identity' ? <IdentityPanel api={api} />
          : panel === 'webhooks' ? <WebhooksPanel api={api} />
            : panel === 'usage' ? <UsagePanel api={api} />
              : panel === 'feature-flags' ? <FeatureFlagsPanel api={api} />
                : <UserManagementPanel api={api} />}
    </div>
  );
}
